/* global axios */
// Outbound Marketing API client.
// Uses the global axios instance (carries the DeviseTokenAuth session headers set on login) —
// a bare fetch() sends no auth headers and 401s.
//
// Every endpoint takes the chatbot id and nothing else that matters: Meta and HubSpot
// credentials are resolved server-side from Firestore and never reach this layer.

const BASE = accountId => `/api/v1/accounts/${accountId}/outbounds`;

const body = r => r.data?.payload;

export default {
  // Agents the signed-in operator may send on behalf of. [{ id, name, url_image, chatbot_phone }]
  chatbots(accountId) {
    return axios.get(`${BASE(accountId)}/chatbots`).then(body);
  },
  // Non-secret view of the resolved integration — which number sends, and whether the
  // Meta/HubSpot tokens are configured at all.
  integration(accountId, chatbotId) {
    return axios
      .get(`${BASE(accountId)}/integration`, {
        params: { chatbot_id: chatbotId },
      })
      .then(body);
  },
  templates(accountId, chatbotId) {
    return axios
      .get(`${BASE(accountId)}/templates`, {
        params: { chatbot_id: chatbotId },
      })
      .then(body);
  },
  segments(accountId, chatbotId) {
    return axios
      .get(`${BASE(accountId)}/segments`, {
        params: { chatbot_id: chatbotId },
      })
      .then(body);
  },
  // Os rótulos da conta. É a fonte de audiência que não depende de CRM nenhum, e por isso não
  // exige agente escolhido — o servidor a isenta do `set_integration`.
  labels(accountId) {
    return axios.get(`${BASE(accountId)}/labels`).then(body);
  },
  // `listId` é segmento do HubSpot; `labelId` é rótulo da conta. Um ou outro, nunca os dois:
  // quem decide é a origem escolhida na tela.
  contacts(accountId, chatbotId, { listId, labelId } = {}) {
    return axios
      .get(`${BASE(accountId)}/contacts`, {
        params: { chatbot_id: chatbotId, list_id: listId, label_id: labelId },
      })
      .then(body);
  },
  // ── rascunho de audiência ─────────────────────────────────────────────────────────────
  // A lista revisada de CSV, planilha e lista manual vive no backend Go. É ela que permite que
  // essas origens virem plano: sem um lugar para a lista, não há o que reler no dia agendado.
  draftCreate(accountId, { chatbotId, source, segmentLabel }) {
    return axios
      .post(`${BASE(accountId)}/drafts`, {
        chatbot_id: chatbotId,
        source,
        segment_label: segmentLabel,
      })
      .then(body);
  },
  // A planilha sobe inteira: quem sabe ler .xls e .xlsx é o Go.
  draftImport(accountId, draftId, file) {
    const form = new FormData();
    form.append('file', file);
    return axios
      .post(`${BASE(accountId)}/drafts/${draftId}/import`, form)
      .then(body);
  },
  draftAddContacts(accountId, draftId, contacts) {
    return axios
      .post(`${BASE(accountId)}/drafts/${draftId}/contacts`, { contacts })
      .then(body);
  },
  draftContacts(accountId, draftId) {
    return axios
      .get(`${BASE(accountId)}/drafts/${draftId}/contacts`)
      .then(body);
  },
  // Disparo como PLANO: cria um Campaign e deixa o servidor materializar e delegar ao backend.
  // Diferente de `dispatch`, que manda a lista direto e não deixa objeto nenhum para trás — sem
  // plano não há agendamento, janela, reprocesso nem relatório por evento.
  // O que o contato vai LER, com um contato de verdade da lista. Quem resolve as variáveis é o
  // backend Go; pedir a prévia a ele evita uma segunda cópia da convenção `{{1}}` = primeiro nome.
  templatePreview(
    accountId,
    { chatbotId, templateName, language, campaignId, contact }
  ) {
    return axios
      .post(`${BASE(accountId)}/templates/preview`, {
        // `chatbot_id` não é opcional: o `set_integration` do controller resolve a credencial
        // a partir dele e recusa a chamada sem ele, antes de chegar na action.
        chatbot_id: chatbotId,
        template_name: templateName,
        language,
        campaign_id: campaignId,
        contact,
      })
      .then(body);
  },

  dispatchAsCampaign(
    accountId,
    {
      chatbotId,
      templateName,
      segment,
      message,
      audience,
      eventoId,
      title,
      scheduledAt,
    }
  ) {
    return axios
      .post(`${BASE(accountId)}/campaigns`, {
        chatbot_id: chatbotId,
        template_name: templateName,
        segment,
        message,
        audience,
        evento_id: eventoId,
        title,
        // Ausente = sai agora. Presente = o backend guarda a campanha `active` e o cron a colhe.
        scheduled_at: scheduledAt,
      })
      .then(body);
  },
  // O que a barra lê. Os contadores vêm de `campaign_dispatches` — o que de fato aconteceu —
  // e não do `sent_count` do disparo, que é quantos contatos foram ENTREGUES ao backend antes
  // de a Meta aceitar qualquer um.
  // Quem recebeu e quem não, com o motivo de cada recusa.
  campaignRecipients(accountId, campaignId) {
    return axios
      .get(`${BASE(accountId)}/campaigns/${campaignId}/recipients`)
      .then(body);
  },

  // Mandar de novo só para quem a Meta recusou. Campanha nova; a antiga fica intacta.
  campaignRetryFailed(accountId, campaignId) {
    return axios
      .post(`${BASE(accountId)}/campaigns/${campaignId}/retry_failed`)
      .then(body);
  },

  // A campanha inteira outra vez, mesma audiência e template.
  campaignRepeat(accountId, campaignId) {
    return axios
      .post(`${BASE(accountId)}/campaigns/${campaignId}/repeat`)
      .then(body);
  },

  campaignProgress(accountId, campaignId) {
    return axios
      .get(`${BASE(accountId)}/campaigns/${campaignId}/progress`)
      .then(body);
  },
  // Histórico por pessoa: quem já recebeu (ou deixou de receber) um disparo.
  // A conta nunca vai na query — o Rails a resolve de Current.account.
  historyPeople(accountId, { q, limit, offset } = {}) {
    return axios
      .get(`${BASE(accountId)}/historico/pessoas`, {
        params: { q, limit, offset },
      })
      .then(body);
  },
  // A linha do tempo de uma pessoa, com o POST de cada tentativa.
  historyPerson(accountId, pessoa, { limit } = {}) {
    return axios
      .get(`${BASE(accountId)}/historico/pessoa`, {
        params: { pessoa, limit },
      })
      .then(body);
  },
  // As execuções (lotes). O mesmo template sai por agentes diferentes, então a
  // execução — não o template — é a unidade que o operador escolhe.
  historyLotes(accountId, { limit, offset } = {}) {
    return axios
      .get(`${BASE(accountId)}/historico/lotes`, { params: { limit, offset } })
      .then(body);
  },
  historyLote(accountId, idLote) {
    return axios
      .get(`${BASE(accountId)}/historico/lote/${encodeURIComponent(idLote)}`)
      .then(body);
  },
  // Retomada individual: manda um template para o contato de uma conversa aberta,
  // pelo número do agente escolhido. O contato sai da própria conversa no Rails —
  // o navegador não escolhe para quem vai.
  sendTemplateToConversation(
    accountId,
    conversationId,
    { chatbotId, templateName }
  ) {
    return axios
      .post(
        `${BASE(accountId)}/conversations/${conversationId}/send_template`,
        {
          chatbot_id: chatbotId,
          template_name: templateName,
        }
      )
      .then(body);
  },
  // Quem, nesta lista, já recebeu um template hoje — em qualquer disparo da conta,
  // feito por qualquer operador. Só lê o banco: não toca na Meta nem no HubSpot.
  dispatchCheck(accountId, { contacts }) {
    return axios
      .post(`${BASE(accountId)}/dispatch_check`, { contacts })
      .then(body);
  },
  // `acknowledgeDuplicates` é o "disparar mesmo assim": sem ele o servidor responde
  // 409 com a lista de repetidos, e é esse 409 que segura o disparo de verdade.
  dispatch(
    accountId,
    {
      chatbotId,
      chatbotName,
      templateName,
      segment,
      contacts,
      acknowledgeDuplicates = false,
    }
  ) {
    return axios
      .post(`${BASE(accountId)}/dispatch`, {
        chatbot_id: chatbotId,
        chatbot_name: chatbotName,
        template_name: templateName,
        segment,
        contacts,
        acknowledge_duplicates: acknowledgeDuplicates,
      })
      .then(body);
  },
};
